"use client";
import { DotLogoSvgIcon } from "@/Assets/icons";
import FormInput from "@/Components/Form/FormInput";
import PhoneNumberInput from "@/Components/Form/PhoneNumberInput/PhoneNumberInput";
import { useSendOtp } from "@/hooks";
import {
  bootstrapApp,
  clearAllCookies,
  sendOtp,
  useFetchLogin,
  useOtpVerificationLogin,
  useRegisterMobileUser,
} from "@/ServerActions";
import { useRetrieveTitle } from "@/Utils/helpers";
import { LeftOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Form, Input, Radio, Row, Spin } from "antd";
import { InputOTP } from "antd-input-otp";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import style from "./style.module.scss";
import { isValidPhoneNumber } from "react-phone-number-input";
import { nearestWhereami } from "@/ServerActions/location";
import { COUNTRIES } from "@/Components/Form/countries";
import { useQueryClient } from "@tanstack/react-query";
import { getCookie, setCookie } from "cookies-next";
import { RawBootstrapService } from "@/Utils/bootstrapMerge";
import { get } from "lodash";

type LoginStep = "LOGIN" | "PASSWORD" | "OTP";

type LoginMethod = "email" | "phone";

interface LoginFormData {
  email?: string;
  phone?: string;
}

interface LoginSubmitData {
  email?: string;
  phone?: string;
}

interface PasswordData {
  password: string;
}

interface OtpData {
  otp: string[];
}

interface CountryState {
  iso: string;
  code: string;
}

const Login = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { getTitle, direction, getMultipleTitles } = useRetrieveTitle();
  const [step, setStep] = useState<LoginStep>("LOGIN");
  const [selectedField, setSelectedField] = useState<LoginMethod>("email");
  const [timeLeft, setTimeLeft] = useState(120);
  const [isActive, setIsActive] = useState(false);
  const [isPhoneNumber, setIsPhoneNumber] = useState("");
  const [formData, setFormData] = useState<LoginFormData>({});
  const [loginLoading, setLoginLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [defaultCountry, setDefaultCountry] =
    useState<CountryState>({
      iso: "SA",
      code: "+966",
    });

  const [defaultServiceId, setDefaultServiceId] = useState("");

  const { mutateAsync: sendOtpResend } = useSendOtp();
  const [pageLoading, setPageLoading] = useState(true);

  const handleLoginOptionChange = (e: any) => {
    setSelectedField(e.target.value);
  };

  // useEffect(() => {
  //   const initTheme = async () => {
  //     try {
  //       setPageLoading(true);

  //       await bootstrapApp();

  //       const whereami = await nearestWhereami();

  //       const isoCode =
  //         whereami?.data?.Country?.IsoCode || "SA";

  //       const matchedCountry =
  //         COUNTRIES.find(
  //           (country) => country.iso === isoCode
  //         ) || {
  //           iso: "SA",
  //           code: "+966",
  //         };

  //       setDefaultCountry(matchedCountry);
  //     } catch (error) {
  //       console.error(error);
  //     } finally {
  //       setPageLoading(false);
  //     }
  //   };

  //   initTheme();
  // }, []);

  useEffect(() => {
    const initTheme = async () => {
      try {
        setPageLoading(true);

        const resp = await bootstrapApp();

        const services: RawBootstrapService[] = get(
          resp,
          "data.data.services",
          []
        );

        console.log("DOT LOGIN BOOTSTRAP SERVICES =>", services);

        if (services.length > 0) {
          // Use existing selected service if available,
          // otherwise use the first active service.
          const selectedServiceId =
            (getCookie("serviceIdDot") as string) || null;

          const activeService =
            services.find(
              (service) =>
                service.service_id === selectedServiceId
            ) ||
            services.find((service) => service.is_active) ||
            services[0];

          const serviceId = activeService?.service_id
            ? String(activeService.service_id)
            : "";

          console.log(
            "DOT LOGIN ACTIVE SERVICE ID =>",
            serviceId
          );

          setDefaultServiceId(serviceId);

          if (serviceId) {
            setCookie("serviceIdDot", serviceId);
          }
        }

        const whereami = await nearestWhereami();

        const isoCode =
          whereami?.data?.Country?.IsoCode || "SA";

        const matchedCountry =
          COUNTRIES.find(
            (country) => country.iso === isoCode
          ) || {
            iso: "SA",
            code: "+966",
          };

        setDefaultCountry(matchedCountry);
      } catch (error) {
        console.error("Login bootstrap error:", error);
      } finally {
        setPageLoading(false);
      }
    };

    initTheme();
  }, []);


  const onFinish = async (
    value: LoginSubmitData
  ) => {
    const { email, phone } = value;
    setLoginLoading(true);

    if (selectedField === "email") {
      setFormData((prev) => ({
        ...prev,
        email,
      }));
      setStep("PASSWORD");
      setLoginLoading(false);
    }



    if (selectedField === "phone") {
      if (!phone) {
        toast.error(getTitle("error_phone_number_empty"));
        setLoginLoading(false);
        return;
      }

      const trimmedPhoneNumber = phone.replace(/\s+/g, "");

      if (!isValidPhoneNumber(trimmedPhoneNumber)) {
        toast.error("Please enter valid number with code!");
        setLoginLoading(false);
        return;
      }

      try {
        const phoneNumber = `${trimmedPhoneNumber}`;
        const response = await useRegisterMobileUser(phoneNumber, defaultServiceId);
        if (response?.error) {
          toast.error(response?.message || "User registration failed");
          setLoginLoading(false);
          return;
        }
        setFormData((prev) => ({
          ...prev,
          phone: phoneNumber,
        }));
        setIsPhoneNumber(phoneNumber);

        const res: any = await sendOtp(phoneNumber);

        if (res?.error) {
          toast.error(res?.message || "OTP sending failed");
          setLoginLoading(false);
          return;
        }
        toast.success(getTitle("otp_sent_successfully"));
        setStep("OTP");
        setIsActive(true);
        setTimeLeft(300);
      } catch (error) {
        toast.error("Something Went Wrong");
        console.error(error);
      } finally {
        setLoginLoading(false);
      }
    }
  };

  const handlePasswordLogin = async (
    value: PasswordData
  ) => {
    setPasswordLoading(true);

    try {
      const res: any = await useFetchLogin(
        {
          email: formData.email,
          password: value.password,
        },
        defaultServiceId
      );

      if (res?.error) {
        toast.error(res?.message || "Login failed");
        return;
      }

      toast.success(getTitle("logged_in_successfully"));
      localStorage.removeItem("dateToggleState");
      queryClient.resetQueries({ queryKey: ["user-checks"] });
      router.push("/");
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleOtpLogin = async (
    value: OtpData
  ) => {
    setOtpLoading(true);

    try {
      const res: any = await useOtpVerificationLogin(
        {
          username: formData.phone,
          otp: value.otp?.join(""),
        },
        defaultServiceId
      );

      if (res?.error) {
        toast.error(res?.message || getTitle("otp_verification_failed"));
        return;
      }
      toast.success(getTitle("logged_in_successfully"));
      localStorage.removeItem("dateToggleState");
      queryClient.resetQueries({ queryKey: ["user-checks"] });
      router.push("/");
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setOtpLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsActive(false);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isActive]);

  const startTimer = async () => {
    try {
      setResendLoading(true);

      setTimeLeft(300);
      setIsActive(true);

      if (isPhoneNumber) {
        await sendOtpResend(isPhoneNumber);

        toast.success(
          getTitle("otp_resent_successfully")
        );
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleBack = () => {
    setStep("LOGIN");
    setTimeLeft(120);
    setIsActive(false);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const clearAll = () => {
    clearAllCookies();
  }

  if (pageLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            textAlign: "center",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <Row className={style.loginContainer}>
      {/* <Button onClick={clearAll}>clearAll</Button> */}
      <Col xs={24} sm={24} md={10} lg={12} xl={12} className={style.imgWrapper}>
        <DotLogoSvgIcon width="200px" height="200px" className={style.logo} />
      </Col>
      <Col sm={24} md={14} lg={12} xl={12} className={style.formWrapper}>
        {step === "LOGIN" && (
          <>
            <Radio.Group
              className={style.SelectOption}
              optionType="button"
              buttonStyle="solid"
              onChange={handleLoginOptionChange}
              value={selectedField}
            >
              <Radio value="email">{getTitle("email_address")}</Radio>
              <Radio value="phone">{getTitle("phone_number")}</Radio>
            </Radio.Group>
            <Form
              name="basic"
              initialValues={formData}
              onFinish={onFinish}
              autoComplete="off"
              layout="vertical"
              className={style.form}
              dir={direction}
            >
              <div>
                <span style={{ fontSize: "36px" }}>👋</span>
                <h1 className={style.heading}>
                  {getTitle("welcome_back")}
                </h1>
                <p>{getTitle("lets_build_something")}</p>
                <div className={style.divider} />
              </div>
              <Divider plain />
              {selectedField === "email" ? (
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: getTitle("please_enter_your_email!"),
                    },
                    {
                      type: "email",
                      message: getTitle("invalid_email_title"),
                    },
                  ]}
                >
                  <FormInput placeholder={getTitle("enter_your_email_placeholder")} />
                </Form.Item>
              ) : (
                <Form.Item
                  name="phone"
                  className={style.input}
                  rules={[{ required: true, message: getTitle("please_input_your_phone_number_error") }]}
                  valuePropName="value"
                  getValueFromEvent={(...args: any[]) => args[0]}
                >
                  <PhoneNumberInput
                    title={getTitle("phone_number")}
                    placeholder={getTitle(
                      "enter_your_phone_number_placeholder"
                    )}
                    defaultCode={defaultCountry.code}
                    defaultCountryIso={defaultCountry.iso}
                  />
                </Form.Item>
              )}
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  className={style.btn}
                  loading={loginLoading}
                >
                  {getTitle("next")}
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
        {step === "PASSWORD" && (
          <>
            <div className={style.backSection}>
              <p>
                <span>
                  <LeftOutlined />
                </span>
                {getTitle("back")}
              </p>
            </div>
            <Form
              name="passwordForm"
              initialValues={formData}
              onFinish={handlePasswordLogin}
              autoComplete="off"
              layout="vertical"
              className={style.form}
              dir={direction}
            >
              <div>
                <span style={{ fontSize: "36px" }}>🔐</span>
                <h1 className={style.heading}>
                  {getTitle("password")}
                </h1>
                <p>{getTitle("enter_your_password")}</p>
                <div className={style.divider} />
              </div>
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: getTitle("enter_your_password_placeholder") },
                ]}
              >
                <FormInput
                  title={getTitle("password")}
                  type="password"
                  size="large"
                  placeholder={getTitle("enter_your_password_placeholder")}
                />
              </Form.Item>
              <Link
                href="#"
                prefetch={false}
                passHref
                className={style.forgotText}
              >
              </Link>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  className={style.btn}
                  loading={passwordLoading}
                >
                  {getTitle("login")}
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
        {step === "OTP" && (
          <>
            <div className={style.backSection}>
              <p onClick={handleBack}>
                <span>
                  <LeftOutlined />
                </span>
                {getTitle("back")}
              </p>
            </div>
            <Form
              name="otpForm"
              initialValues={{ remember: true }}
              onFinish={handleOtpLogin}
              autoComplete="off"
              layout="vertical"
              dir={direction}
              className={style.form}
            >
              <div>
                <span style={{ fontSize: "36px" }}>🔐</span>
                <h1 className={style.heading}>{getTitle("otp")}</h1>
                <p>{getTitle("enter_your_otp")}</p>
                <div className={style.divider} />
              </div>
              <Form.Item
                name="otp"
                className="center-error-message"
                rules={[{ required: true, message: getTitle("please_enter_your_otp") }]}
              >
                <InputOTP
                  size="small"
                  autoFocus
                  inputType="numeric"
                  length={6}
                />
              </Form.Item>
              {isActive && (
                <h4 className={style.timerText}>
                  {getTitle("timer")}: {formatTime(timeLeft)}
                </h4>
              )}
              <Form.Item>
                {isActive ? (
                  <>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      className={style.btn}
                      loading={otpLoading}
                    >
                      {getTitle("verify")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="primary"
                      htmlType="submit"
                      block
                      className={style.btn}
                      onClick={startTimer}
                      loading={resendLoading}
                    >
                      {getMultipleTitles("resend", "otp")}
                    </Button>
                  </>
                )}
              </Form.Item>
            </Form>
          </>
        )}
      </Col>
    </Row>
  );
};

export default Login;